import { Inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, forkJoin, map, switchMap } from 'rxjs';
import { FileServiceConfig, StoredFile } from '../lib/file-service.types';
import { PagedResponse } from '@bioconnect-ui/shared-service'


@Injectable({
  providedIn: 'root',
})
export class FileService {
  constructor(
    private httpClient: HttpClient,
    @Inject('fileServiceConfig') private fileServiceConfig: FileServiceConfig
  ) {}

  private restServer = this.fileServiceConfig.restServer;

  /**
   * Retrieves a paged response of files from the server based on the given page number
   * @param pageNumber The page number to retrieve
   * @returns An observable of the paged response
   */
  getFilesByPage(pageNumber: number): Observable<PagedResponse> {
    return this.httpClient.get<PagedResponse>(
      `${this.restServer}/core_files/stored_file/?page=${pageNumber}`
    );
  }

  /**
   * Retrieves a single stored file record based on ID
   * @param id The stored file's primary key
   * @returns An observable of StoredFile
   */
  getFileById(id: number): Observable<StoredFile> {
    return this.httpClient.get<StoredFile>(
      `${this.restServer}/core_files/stored_file/${id}/`
    );
  }

  /**
   * Returns a string observable representing the upload URL for a file with the given name and content type.
   * @param fileName - The name of the file to for which the upload url will be generated
   * @param contentType - The content type of the file to upload
   * @returns A string observable that respresents the upload URL
   */
  getUploadURL(fileName: string, contentType: string): Observable<string> {
    return this.httpClient.get<string>(
      `${this.restServer}/core_files/stored_file/get_upload_url/?file_name=${fileName}&content_type=${contentType}`,
      { headers: { 'skip-error-interceptor': 'true' }}
    );
  }

  /**
   * Downloads a file with the given file name using a download url
   * generated by the file storage library
   * @param fileName - The name of the file to download
   * @returns An observable that emits the downloaded file
   */
  downloadFile(fileName: string): any {
    return this.httpClient
      .get<string>(
        `${this.restServer}/core_files/stored_file/get_download_url/?file_name=${fileName}`
      )
      .pipe(
        switchMap((url: any) => {
          return this.httpClient.get(url.location, { responseType: 'blob'});
        })
      );
  }

  /**
   * Gets file data as a series of arrays for each row (starting with headers)
   * @param fileName - The name of the file to download
   * @returns An observable that emits the downloaded file
   */
  getFileData(fileName: string): any {
    return this.httpClient
      .get<any>(
        `${this.restServer}/core_files/stored_file/get_file_data/?file_name=${fileName}`
      )
  }

  /**
   * Retrieves a paged response of files
   * @param fileName - The name of the file to search for. If empty, all files will be returned.
   * @param pageNumber - The page number of the results to retrieve.
   * @returns An Observable of type PagedResponse.
   */
  getFiles(user: string, filter: string, pageNumber: number, sort: string, direction: string, pageSize: number, permission='can_view'): Observable<PagedResponse> {
      return this.httpClient.get<PagedResponse>(
        `${this.restServer}/core_files/stored_file/?filter=${filter}&page=${pageNumber}&page_size=${pageSize}&user=${user}&sort=${sort}&direction=${direction}&permission=${permission}`
    );
  }

  /**
   * Retrieves an array of stored files based on the provided file IDs.
   *
   * @param fileIds - An array of file IDs.
   * @returns An observable that emits an array of StoredFile objects.
   */
  getFilesByIds(fileIds: number[]): Observable<StoredFile[]> {
    return this.httpClient.post<StoredFile[]>(
      `${this.restServer}/core_files/stored_file/get_by_ids/`,
      fileIds
    );
  }

  /**
   * Gets a unique filename for a given file
   * @param fileName - The filename to get a unique name for
   * @returns An observable of the unique filename
   */
  getUniqueFilename(fileName: string): Observable<string> {
    return this.httpClient.get<string>(
      `${this.restServer}/core_files/stored_file/get_unique_filename/?file_name=${fileName}`,
      { headers: { 'skip-error-interceptor': 'true' }}
    );
  }

  /**
   * Uploads a file using a file upload URL and creates the
   * associated stored file record
   * @param fileUploadURL - The URL to upload the file to
   * @param fileName - The unique file name for storage
   * @param fileToUpload - The file to upload
   * @param userName - The name of the user uploading the file
   * @param contentType - The MIME type of the file
   * @returns An observable of the new stored file
   */
  addFile(fileUploadURL: string, fileName: string, fileToUpload: File, userName: string, contentType: string): Observable<StoredFile> {
    const headers = new HttpHeaders({
      'Content-Type': contentType,
      'skip-error-interceptor': 'true'
    });
    const lastModifiedDate = new Date(fileToUpload.lastModified);
    const addedFile: StoredFile = {
      filename: fileName,
      file_last_modified: lastModifiedDate.toISOString(),
      size: fileToUpload.size,
      uploaded_by_name: userName
    };

    // TODO: Decide on the process for dealing with errors from either
    // of these calls.  For example, should we create a stored file
    // record even if the upload fails?
    return forkJoin([
      this.httpClient.put(fileUploadURL, fileToUpload, { headers: headers }),
      this.httpClient.post<StoredFile>(
        `${this.restServer}/core_files/stored_file/`,
        addedFile
      ),
    ]).pipe(map((results) => results[1]));
  }

  deleteFile(id: number) {
    return this.httpClient.delete(
      `${this.restServer}/core_files/stored_file/${id}/`,
      { headers: { 'skip-error-interceptor': 'true' }}
    );
  }
  
  /**
   * Get the count of associated files for each file type
   * @return An observable containing the file type counts
   */
  getFileTypeCounts(): Observable<any> {
    return this.httpClient.get(
      `${this.restServer}/metadata_repository/data/filetype_counts/`
    );
  }

  /**
   * Get the count of files for a specific file type
   * @param fileTypeId - The ID of the file type
   * @returns An observable containing the count as a number
   */
  getFileTypeCountById(fileTypeId: number): Observable<number> {
    return this.httpClient.get<{ count: number }>(
      `${this.restServer}/metadata_repository/data/filetype_count/?filetype_id=${fileTypeId}`
    ).pipe(
      map(response => response.count)
    );
  }
}
